import type { Concert, ProgrammeItem } from '@brent/shared/types'
import type { ConcertPerformer } from '@/types/data'
import apiClient from './client'

// --- List / Detail ---

export interface ConcertListResponse {
  concerts: Concert[]
}

export interface ConcertDetailResponse {
  concert: Concert
  programme: ProgrammeItem[]
  performers: ConcertPerformer[]
}

export interface FetchConcertsParams {
  year?: number
  decade?: number
}

export async function fetchConcerts(params?: FetchConcertsParams): Promise<Concert[]> {
  const searchParams = new URLSearchParams()
  if (params?.year) searchParams.set('year', String(params.year))
  if (params?.decade) searchParams.set('decade', String(params.decade))
  const qs = searchParams.toString()
  const path = qs ? `/concerts?${qs}` : '/concerts'
  const response = await apiClient.get<ConcertListResponse>(path)
  return response.concerts
}

export async function fetchConcert(id: number): Promise<ConcertDetailResponse> {
  return apiClient.get<ConcertDetailResponse>(`/concerts/${id}`)
}

// --- Update ---

export interface UpdateConcertInput {
  quality_score?: string
  date_text?: string
  date_iso?: string | null
  year?: number
  venue_name?: string
  ensemble_name?: string
  notes?: string | null
}

export interface UpdateConcertResponse {
  ok: boolean
  error?: string
}

export async function updateConcert(
  id: number,
  input: UpdateConcertInput,
): Promise<UpdateConcertResponse> {
  return apiClient.put<UpdateConcertResponse>(`/concerts/${id}`, input)
}

// --- Create ---

export interface CreateConcertInput {
  date_text: string
  year: number
  date_iso?: string
  venue_name?: string
  ensemble_name?: string
  notes?: string
}

export interface CreateConcertResponse {
  ok: boolean
  concert: Concert
  id: number
  error?: string
}

export async function createConcert(input: CreateConcertInput): Promise<CreateConcertResponse> {
  return apiClient.post<CreateConcertResponse>('/concerts', input)
}

// --- Programme items ---

export interface AddProgrammeItemInput {
  work_id?: number
  work_title?: string
  composer_name?: string
  work_qid?: string
  composer_qid?: string
  arrangement_note?: string
  notes?: string
}

export interface AddProgrammeItemResponse {
  ok: boolean
  programme_item: ProgrammeItem
  id: number
  error?: string
}

export async function addProgrammeItem(
  concertId: number,
  input: AddProgrammeItemInput,
): Promise<AddProgrammeItemResponse> {
  return apiClient.post<AddProgrammeItemResponse>(`/concerts/${concertId}/programme`, input)
}

export interface DeleteProgrammeItemResponse {
  ok: boolean
  error?: string
}

export async function deleteProgrammeItem(
  concertId: number,
  itemId: number,
): Promise<DeleteProgrammeItemResponse> {
  return apiClient.delete<DeleteProgrammeItemResponse>(`/concerts/${concertId}/programme/${itemId}`)
}

export interface ReorderProgrammeResponse {
  ok: boolean
  programme: ProgrammeItem[]
  error?: string
}

export async function reorderProgramme(
  concertId: number,
  itemIds: number[],
): Promise<ReorderProgrammeResponse> {
  return apiClient.put<ReorderProgrammeResponse>(`/concerts/${concertId}/programme/reorder`, {
    item_ids: itemIds,
  })
}

// --- Wikidata work lookup ---

export interface WikidataWorkResponse {
  ok: boolean
  title?: string
  composer_name?: string
  composer_qid?: string
  error?: string
}

export async function lookupWikidataWork(qid: string): Promise<WikidataWorkResponse> {
  return apiClient.get<WikidataWorkResponse>(`/wikidata/work/${qid}`)
}
